import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, ChevronUp, ChevronsUpDown, Users, UserPlus, Upload, Link2, Mail, Shield, Search, Filter, MoreVertical, Edit, Trash2, CheckCircle, XCircle, Copy, Download, FileSpreadsheet, AlertCircle, Clock, TrendingUp, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
import { SkillsOverview } from '../components/SkillsOverview';
type UserSortKey = 'name' | 'status' | 'skills' | 'lastActive';
export function UserManagementPage() {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userType, setUserType] = useState<'learners' | 'admins'>('learners');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [generatedLink, setGeneratedLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const licenseDropdownRef = useRef<HTMLDivElement>(null);
  const linkLicenseDropdownRef = useRef<HTMLDivElement>(null);
  const paginationDropdownRef = useRef<HTMLDivElement>(null);
  const actionMenuRefs = useRef<{
    [key: number]: HTMLDivElement | null;
  }>({});
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('learner');
  const [selectedLicenses, setSelectedLicenses] = useState<string[]>([]);
  const [isLicenseDropdownOpen, setIsLicenseDropdownOpen] = useState(false);
  const [isPaginationDropdownOpen, setIsPaginationDropdownOpen] = useState(false);
  const [licenseSearch, setLicenseSearch] = useState('');
  const [addModalStep, setAddModalStep] = useState<'form' | 'confirm'>('form');
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);
  const [selectedModalPathways, setSelectedModalPathways] = useState<string[]>([]);
  const [linkLicenses, setLinkLicenses] = useState<string[]>([]);
  const [isLinkLicenseDropdownOpen, setIsLinkLicenseDropdownOpen] = useState(false);
  const [linkLicenseSearch, setLinkLicenseSearch] = useState('');
  const [linkPathways, setLinkPathways] = useState<string[]>([]);
  // New State for Bulk Actions
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [bulkUploadStep, setBulkUploadStep] = useState<'upload' | 'confirm'>('upload');
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [bulkUploadError, setBulkUploadError] = useState('');
  const [bulkImportedCount, setBulkImportedCount] = useState(0);
  const [bulkSelectedSkills, setBulkSelectedSkills] = useState<string[]>([]);
  const [bulkSelectedPathways, setBulkSelectedPathways] = useState<string[]>([]);
  const [isBulkSkillDropdownOpen, setIsBulkSkillDropdownOpen] = useState(false);
  const [isBulkPathwayDropdownOpen, setIsBulkPathwayDropdownOpen] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showNoLicenseToast, setShowNoLicenseToast] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const mobileFilterRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [sortKey, setSortKey] = useState<UserSortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [adminModalStep, setAdminModalStep] = useState<'form' | 'confirm'>('form');
  const [adminEntries, setAdminEntries] = useState([{
    firstName: '',
    lastName: '',
    email: ''
  }]);
  const [adminAcknowledge, setAdminAcknowledge] = useState(false);
  const bulkSkillDropdownRef = useRef<HTMLDivElement>(null);
  const bulkPathwayDropdownRef = useRef<HTMLDivElement>(null);
  const bulkUploadInputRef = useRef<HTMLInputElement>(null);
  const licensesRemaining = 25;
  const totalLicenses = 280;
  const noLicensesAvailable = licensesRemaining === 0;
  const availableSkills = ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations and Conflict Resolution', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'];
  const availablePathways = ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery'];
  const departments = ['Marketing', 'Human Resources', 'IT', 'Sales', 'Finance', 'Operations'];
  const pathwayData = [{
    name: 'Leadership Essentials',
    skillCount: 3
  }, {
    name: 'DEI Fundamentals',
    skillCount: 2
  }, {
    name: 'Technical Foundations',
    skillCount: 4
  }, {
    name: 'Communication Mastery',
    skillCount: 3
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Close license dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (licenseDropdownRef.current && !licenseDropdownRef.current.contains(event.target as Node)) {
        setIsLicenseDropdownOpen(false);
      }
      if (linkLicenseDropdownRef.current && !linkLicenseDropdownRef.current.contains(event.target as Node)) {
        setIsLinkLicenseDropdownOpen(false);
      }
      if (bulkSkillDropdownRef.current && !bulkSkillDropdownRef.current.contains(event.target as Node)) {
        setIsBulkSkillDropdownOpen(false);
      }
      if (bulkPathwayDropdownRef.current && !bulkPathwayDropdownRef.current.contains(event.target as Node)) {
        setIsBulkPathwayDropdownOpen(false);
      }
      // Close action menu if clicking outside
      if (openActionMenuId !== null) {
        const menuRef = actionMenuRefs.current[openActionMenuId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenActionMenuId(null);
        }
      }
      // Close mobile filter dropdown if clicking outside
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(event.target as Node)) {
        setIsMobileFilterOpen(false);
      }
      if (paginationDropdownRef.current && !paginationDropdownRef.current.contains(event.target as Node)) {
        setIsPaginationDropdownOpen(false);
      }
    };
    if (isLicenseDropdownOpen || isLinkLicenseDropdownOpen || isBulkSkillDropdownOpen || isBulkPathwayDropdownOpen || openActionMenuId !== null || isMobileFilterOpen || isPaginationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLicenseDropdownOpen, isLinkLicenseDropdownOpen, isBulkSkillDropdownOpen, isBulkPathwayDropdownOpen, openActionMenuId, isMobileFilterOpen, isPaginationDropdownOpen]);
  useEffect(() => {
    const startAssignment = () => {
      setSelectedUsers([1]);
      document.querySelector('[data-phoebe-tour="learner-list"]')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    };
    window.addEventListener('phoebe:assign-learning', startAssignment);
    return () => window.removeEventListener('phoebe:assign-learning', startAssignment);
  }, []);
  // Mock data
  const quickStats = [{
    label: 'Total Learners',
    value: '248',
    change: '+12',
    trend: 'up',
    icon: Users
  }, {
    label: 'Active Admins',
    value: '8',
    change: '+2',
    trend: 'up',
    icon: Shield
  }, {
    label: 'Pending Invites',
    value: '18',
    change: '-5',
    trend: 'down',
    icon: Mail
  }, {
    label: 'Registered This Month',
    value: '32',
    change: '+8',
    trend: 'up',
    icon: TrendingUp
  }];
  const [learners, setLearners] = useState([{
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    department: 'Marketing',
    cohort: 'Leadership Track',
    status: 'active',
    skills: 4,
    lastActive: '2024-01-15',
    joinDate: '2023-09-12',
    assignedSkills: ['Active and Empathetic Listening', 'Effective Communication', 'EQ for the Workplace', 'Developing EQ and Empathy'],
    assignedPathways: ['Leadership Essentials', 'Communication Mastery']
  }, {
    id: 2,
    name: 'Michael Chen',
    email: 'mchen@example.com',
    department: 'IT',
    cohort: 'Technical Upskilling',
    status: 'inactive',
    skills: 3,
    lastActive: '2023-12-01',
    joinDate: '2023-10-05',
    assignedSkills: ['AI Functionality Beyond the Hype', 'Effective Communication', 'EQ for the Workplace'],
    assignedPathways: ['Technical Foundations']
  }, {
    id: 3,
    name: 'Emily Davis',
    email: 'emily.d@example.com',
    department: 'Human Resources',
    cohort: '2024 Q1 Onboarding',
    status: 'pending',
    skills: 0,
    lastActive: 'Never',
    joinDate: '2024-01-10',
    assignedSkills: [],
    assignedPathways: []
  }, {
    id: 4,
    name: 'Rachel Green',
    email: 'rachel.g@example.com',
    department: 'Sales',
    cohort: 'Sales Enablement',
    status: 'not_assigned',
    skills: 0,
    lastActive: '2024-01-08',
    joinDate: '2023-07-20',
    assignedSkills: [],
    assignedPathways: []
  }, {
    id: 8,
    name: 'David Park',
    email: 'david.p@example.com',
    department: 'Operations',
    cohort: 'Leadership Track',
    status: 'not_started',
    skills: 2,
    lastActive: '2024-01-05',
    joinDate: '2023-12-15',
    assignedSkills: ['DEI: Employee Relations and Conflict Resolution', 'Developing EQ and Empathy'],
    assignedPathways: ['Leadership Essentials']
  }, {
    id: 9,
    name: 'Anna Martinez',
    email: 'anna.m@example.com',
    department: 'Finance',
    cohort: 'Leadership Track',
    status: 'completed',
    skills: 6,
    lastActive: '2024-01-14',
    joinDate: '2023-06-01',
    assignedSkills: ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations and Conflict Resolution', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'],
    assignedPathways: ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery']
  }, {
    id: 10,
    name: 'Brian Adams',
    email: 'brian.adams@example.com',
    department: 'Sales',
    cohort: 'Sales Enablement',
    status: 'active',
    skills: 2,
    lastActive: '2024-01-12',
    joinDate: '2023-11-08',
    assignedSkills: ['Effective Communication', 'Active and Empathetic Listening'],
    assignedPathways: []
  }, {
    id: 11,
    name: 'Priya Nair',
    email: 'priya.nair@example.com',
    department: 'IT',
    cohort: '2024 Q1 Onboarding',
    status: 'pending',
    skills: 0,
    lastActive: 'Never',
    joinDate: '2024-01-18',
    assignedSkills: [],
    assignedPathways: []
  }, {
    id: 12,
    name: 'Marcus Wilson',
    email: 'marcus.wilson@example.com',
    department: 'Operations',
    cohort: 'Technical Upskilling',
    status: 'not_started',
    skills: 3,
    lastActive: 'Never',
    joinDate: '2023-12-20',
    assignedSkills: ['Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'],
    assignedPathways: []
  }, {
    id: 13,
    name: 'Olivia Brooks',
    email: 'olivia.brooks@example.com',
    department: 'Marketing',
    cohort: '2024 Q1 Onboarding',
    status: 'inactive',
    skills: 1,
    lastActive: '2023-11-28',
    joinDate: '2023-08-14',
    assignedSkills: ['AI Functionality Beyond the Hype'],
    assignedPathways: []
  }]);
  const admins = [{
    id: 1,
    name: 'Linda Taylor',
    email: 'linda.taylor@example.com',
    status: 'active',
    role: 'Super Admin',
    lastActive: '2024-01-15',
    joinDate: '2023-01-10'
  }, {
    id: 2,
    name: 'Robert Martinez',
    email: 'robert.m@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-14',
    joinDate: '2023-03-22'
  }, {
    id: 3,
    name: 'Jennifer Lee',
    email: 'jennifer.lee@example.com',
    status: 'active',
    role: 'Admin',
    lastActive: '2024-01-13',
    joinDate: '2023-05-18'
  }];
  const handleSort = (key: UserSortKey) => {
    if (sortKey === key) {
      setSortDir(direction => direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setExpandedUserId(null);
    setCurrentPage(1);
  };
  const isActiveSort = (key: UserSortKey) => sortKey === key;
  const SortArrow = ({
    columnKey
  }: {
    columnKey: UserSortKey;
  }) => {
    if (!isActiveSort(columnKey)) {
      return <ChevronsUpDown size={14} aria-hidden="true" className="text-gray-400" data-id="element-2765" data-mp="36hwe3pxlt1iv" />;
    }
    return sortDir === 'asc' ? <ChevronUp size={14} aria-hidden="true" className="text-[#30718D]" data-id="element-2766" data-mp="3ge934ai7er7a" /> : <ChevronDown size={14} aria-hidden="true" className="text-[#30718D]" data-id="element-2767" data-mp="3gcelb528bf7f" />;
  };
  const headerButtonClass = (key: UserSortKey) => `flex items-center gap-1 text-left text-sm font-medium transition-colors ${isActiveSort(key) ? 'text-[#30718D]' : 'text-gray-700 hover:text-gray-900'}`;
  const headerActiveClass = (key: UserSortKey) => isActiveSort(key) ? 'bg-[#30718D]/10' : '';
  const cellActiveClass = (key: UserSortKey) => isActiveSort(key) ? 'bg-[#30718D]/5' : '';
  const filteredUsers = (userType === 'learners' ? learners : admins).filter(user => {
    const normalizedSearch = searchQuery.toLowerCase();
    const matchesSearch = user.name.toLowerCase().includes(normalizedSearch) || user.email.toLowerCase().includes(normalizedSearch);
    const matchesFilter = selectedFilter === 'all' || (selectedFilter === 'not_assigned' ? 'assignedSkills' in user && Array.isArray((user as {
      assignedSkills?: string[];
    }).assignedSkills) && (user as {
      assignedSkills?: string[];
    }).assignedSkills?.length === 0 : user.status === selectedFilter);
    return matchesSearch && matchesFilter;
  }).sort((a, b) => {
    const getSortValue = (user: typeof a): string | number => {
      switch (sortKey) {
        case 'status':
          return user.status;
        case 'skills':
          return 'skills' in user ? user.skills : 0;
        case 'lastActive':
          return user.lastActive === 'Never' ? 0 : new Date(user.lastActive).getTime();
        case 'name':
        default:
          return user.name;
      }
    };
    const valueA = getSortValue(a);
    const valueB = getSortValue(b);
    const comparison = typeof valueA === 'number' && typeof valueB === 'number' ? valueA - valueB : String(valueA).localeCompare(String(valueB), undefined, {
      sensitivity: 'base'
    });
    return sortDir === 'asc' ? comparison : -comparison;
  });
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedUsers = filteredUsers.slice((safeCurrentPage - 1) * itemsPerPage, safeCurrentPage * itemsPerPage);
  const handleGenerateLink = () => {
    const linkType = userType === 'learners' ? 'learner' : 'admin';
    const newLink = `https://platform.example.com/register/${linkType}?token=${Math.random().toString(36).substr(2, 9)}`;
    setGeneratedLink(newLink);
  };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };
  const handleDownloadCsvTemplate = () => {
    const csv = 'First Name,Last Name,Email\nTaylor,Smith,taylor.smith@example.com\nJordan,Lee,jordan.lee@example.com';
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'learner-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const handleAddUser = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) return;
    const newLearner = {
      id: Date.now(),
      name: `${firstName.trim()} ${lastName.trim()}`,
      email: email.trim(),
      status: 'pending',
      skills: selectedLicenses.length + selectedModalPathways.reduce((sum, pathway) => sum + (pathwayData.find(item => item.name === pathway)?.skillCount || 0), 0),
      lastActive: 'Never',
      joinDate: new Date().toISOString().split('T')[0],
      assignedSkills: selectedLicenses,
      assignedPathways: selectedModalPathways
    };
    setLearners(prev => [newLearner, ...prev]);
    setSelectedUsers([newLearner.id]);
    setToastMessage(`${newLearner.name} was added.`);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 4000);
    setShowAddModal(false);
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('learner');
    setSelectedLicenses([]);
    setSelectedModalPathways([]);
    setLicenseSearch('');
    setAddModalStep('form');
    setAcknowledgeChecked(false);
  };
  const handleAddAdminEntry = () => {
    setAdminEntries([...adminEntries, {
      firstName: '',
      lastName: '',
      email: ''
    }]);
  };
  const handleAdminEntryChange = (index: number, field: string, value: string) => {
    const newEntries = [...adminEntries];
    newEntries[index] = {
      ...newEntries[index],
      [field]: value
    };
    setAdminEntries(newEntries);
  };
  const handleAdminSubmit = () => {
    const allFilled = adminEntries.every(entry => entry.firstName && entry.lastName && entry.email);
    if (allFilled && adminAcknowledge) {
      setAdminModalStep('confirm');
    }
  };
  const handleCloseAdminModal = () => {
    setShowAddAdminModal(false);
    setAdminModalStep('form');
    setAdminEntries([{
      firstName: '',
      lastName: '',
      email: ''
    }]);
    setAdminAcknowledge(false);
  };
  const toggleLicense = (skill: string) => {
    setSelectedLicenses(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const toggleModalPathway = (pathway: string) => {
    setSelectedModalPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const selectAllModalPathways = () => {
    const allPathwayNames = pathwayData.map(p => p.name);
    if (selectedModalPathways.length === allPathwayNames.length) {
      setSelectedModalPathways([]);
    } else {
      setSelectedModalPathways([...allPathwayNames]);
    }
  };
  const selectAllModalSkills = () => {
    if (selectedLicenses.length === availableSkills.length) {
      setSelectedLicenses([]);
    } else {
      setSelectedLicenses([...availableSkills]);
    }
  };
  const toggleLinkPathway = (pathway: string) => {
    setLinkPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const selectAllLinkPathways = () => {
    const allPathwayNames = pathwayData.map(p => p.name);
    if (linkPathways.length === allPathwayNames.length) {
      setLinkPathways([]);
    } else {
      setLinkPathways([...allPathwayNames]);
    }
  };
  const selectAllLinkSkills = () => {
    if (linkLicenses.length === availableSkills.length) {
      setLinkLicenses([]);
    } else {
      setLinkLicenses([...availableSkills]);
    }
  };
  const toggleLinkLicense = (skill: string) => {
    setLinkLicenses(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  // Bulk Actions Handlers
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (noLicensesAvailable) {
      e.preventDefault();
      setShowNoLicenseToast(true);
      setTimeout(() => setShowNoLicenseToast(false), 3000);
      return;
    }
    const pageUserIds = paginatedUsers.map(user => user.id);
    if (e.target.checked) {
      setSelectedUsers(previous => [...new Set([...previous, ...pageUserIds])]);
    } else {
      setSelectedUsers(previous => previous.filter(userId => !pageUserIds.includes(userId)));
    }
  };
  const handleSelectAllFilteredUsers = () => {
    setSelectedUsers(filteredUsers.map(user => user.id));
  };
  const handleDeselectAllFilteredUsers = () => {
    const filteredUserIds = filteredUsers.map(user => user.id);
    setSelectedUsers(previous => previous.filter(userId => !filteredUserIds.includes(userId)));
  };
  const handleSelectUser = (id: number) => {
    if (noLicensesAvailable) {
      setShowNoLicenseToast(true);
      setTimeout(() => setShowNoLicenseToast(false), 3000);
      return;
    }
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(userId => userId !== id) : [...prev, id]);
  };
  const toggleBulkSkill = (skill: string) => {
    if (skill === 'all') {
      if (bulkSelectedSkills.length === availableSkills.length) {
        setBulkSelectedSkills([]);
      } else {
        setBulkSelectedSkills([...availableSkills]);
      }
      return;
    }
    setBulkSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const toggleBulkPathway = (pathway: string) => {
    if (pathway === 'all') {
      if (bulkSelectedPathways.length === availablePathways.length) {
        setBulkSelectedPathways([]);
      } else {
        setBulkSelectedPathways([...availablePathways]);
      }
      return;
    }
    setBulkSelectedPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const handleBulkAssign = () => {
    // Simulate assigning skills/pathways
    const totalAssigned = bulkSelectedSkills.length + bulkSelectedPathways.length;
    if (totalAssigned === 0) return;
    setLearners(prev => prev.map(learner => {
      if (selectedUsers.includes(learner.id)) {
        return {
          ...learner,
          skills: learner.skills + totalAssigned,
          status: learner.status === 'pending' ? 'inactive' : learner.status,
          assignedSkills: [...(learner.assignedSkills || []), ...bulkSelectedSkills],
          assignedPathways: [...(learner.assignedPathways || []), ...bulkSelectedPathways]
        };
      }
      return learner;
    }));
    setToastMessage(`Successfully assigned ${totalAssigned} items to ${selectedUsers.length} learners.`);
    setShowSuccessToast(true);
    setTimeout(() => setShowSuccessToast(false), 3000);
    // Reset state
    setSelectedUsers([]);
    setBulkSelectedSkills([]);
    setBulkSelectedPathways([]);
    setIsBulkSkillDropdownOpen(false);
    setIsBulkPathwayDropdownOpen(false);
  };
  const handleBulkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setBulkUploadFile(null);
      setBulkUploadError('Choose a CSV file to continue.');
      return;
    }
    setBulkUploadFile(file);
    setBulkUploadError('');
  };
  const handleBulkImport = async () => {
    if (!bulkUploadFile) {
      setBulkUploadError('Choose a CSV file before uploading.');
      return;
    }
    const csvText = await bulkUploadFile.text();
    const [headerRow, ...dataRows] = csvText.trim().split(/\r?\n/);
    const headers = headerRow?.split(',').map(header => header.trim().toLowerCase()) ?? [];
    const firstNameIndex = headers.findIndex(header => header === 'first name' || header === 'firstname');
    const lastNameIndex = headers.findIndex(header => header === 'last name' || header === 'lastname');
    const emailIndex = headers.findIndex(header => header === 'email' || header === 'email address');
    if (firstNameIndex < 0 || lastNameIndex < 0 || emailIndex < 0) {
      setBulkUploadError('Your CSV needs First Name, Last Name, and Email headers.');
      return;
    }
    const newLearners = dataRows.map(row => row.split(',').map(value => value.trim())).filter(values => values[firstNameIndex] && values[lastNameIndex] && values[emailIndex]).map((values, index) => ({
      id: Date.now() + index,
      name: `${values[firstNameIndex]} ${values[lastNameIndex]}`,
      email: values[emailIndex],
      status: 'pending',
      skills: 0,
      lastActive: 'Never',
      joinDate: new Date().toISOString().split('T')[0],
      assignedSkills: [],
      assignedPathways: []
    }));
    if (newLearners.length === 0) {
      setBulkUploadError('We could not find any complete learner rows to import.');
      return;
    }
    setLearners(previous => [...newLearners, ...previous]);
    setSelectedUsers(newLearners.map(learner => learner.id));
    setBulkImportedCount(newLearners.length);
    setBulkUploadStep('confirm');
    setCurrentPage(1);
  };
  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setBulkUploadStep('upload');
    setBulkUploadFile(null);
    setBulkUploadError('');
    setBulkImportedCount(0);
  };
  return <div className="flex-1 bg-gray-50 min-h-screen relative pb-24" data-id="element-2768" data-mp="l2cziwg7eewo">
      {/* Success Toast */}
      <AnimatePresence data-id="element-2769" data-mp="g4bisi692rod">
        {showSuccessToast && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#007558] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3" data-id="element-2770" data-mp="tw83w29i41b9">
            <CheckCircle size={20} data-id="element-2771" data-mp="yvuh4bpbxhk2" />
            <span className="font-medium" data-id="element-2772" data-mp="ywvwuthais84">{toastMessage}</span>
          </motion.div>}
      </AnimatePresence>

      {/* No License Toast */}
      <AnimatePresence data-id="element-2773" data-mp="jhz1s0coz39">
        {showNoLicenseToast && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-[#DB3725] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-md" data-id="element-2774" data-mp="kb9b2yiq6q5">
            <AlertCircle size={20} className="flex-shrink-0" data-id="element-2775" data-mp="faieigzilt8w" />
            <span className="font-medium" data-id="element-2776" data-mp="k6ftojz1ktk3">
              No licenses remaining. Please purchase additional licenses to
              assign skills or pathways.
            </span>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2777" data-mp="2q0gyusbnr01i">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2778" data-mp="2q20ecgphp728">
          <div className="flex items-center h-16" data-id="element-2779" data-mp="2uyqvl7d168p8">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2780" data-mp="2nfrcl2thej6e">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2781" data-mp="2iosupysuhd11">
              <Link to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2782" data-mp="2dq7sxuxy2lah">
                <Home size={18} data-id="element-2783" data-mp="28s68p6os8fxf" />
                <span className="hidden sm:inline" data-id="element-2784" data-mp="28p1xvqgtalxs">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2785" data-mp="36edgsqkaoqej">
                <BarChart3 size={18} data-id="element-2786" data-mp="2wgfsk32fqtlx" />
                <span className="hidden sm:inline" data-id="element-2787" data-mp="2wfedlniatmyx">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2788" data-mp="2se2mtenouvp5">
                <FileText size={18} data-id="element-2789" data-mp="1kzg2le3k8qv2" />
                <span className="hidden sm:inline" data-id="element-2790" data-mp="1gzfjhg5inrho">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2791" data-mp="3g4yu67r7xlpf">
                <UserCog size={18} data-id="element-2792" data-mp="3l1fjh5a5m643" />
                <span className="hidden sm:inline" data-id="element-2793" data-mp="374kbzh9jwmq5">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2794" data-mp="3c3dlyzpmpvlk">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2795" data-mp="3od5z43nyde7">
                <Bell size={20} data-id="element-2796" data-mp="7odujhkh84yz" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2797" data-mp="7qq8ljn45v6r"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2798" data-mp="3v1pzexnkprmy">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2799" data-mp="3v054v819e8p3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2800" data-mp="rdxzggxg0t1q">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2801" data-mp="wqmkgnkqhi9l">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2802" data-mp="1agq0uxwxmhr4" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2803" data-mp="15kr8kz8hgflf">
                    <div className="py-2" data-id="element-2804" data-mp="drqszk09fhzg">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2805" data-mp="dstknu5epogf">
                        <Users size={18} className="text-gray-600" data-id="element-2806" data-mp="mrdwqcggozvo" />
                        <span className="font-medium text-gray-900" data-id="element-2807" data-mp="mu9v21w81qkn">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2808" data-mp="1y6am5ngk5r3k">
                        <Home size={18} className="text-gray-600" data-id="element-2809" data-mp="3n5eggvg6d0p8" />
                        <span className="font-medium text-gray-900" data-id="element-2810" data-mp="3i74k4hipru21">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2811" data-mp="398riqqgee6i9">
                        <UserCog size={18} className="text-gray-600" data-id="element-2812" data-mp="9s2yfba5oca7" />
                        <span className="font-medium text-gray-900" data-id="element-2813" data-mp="5sc2kt5rv5yt">
                          Admin Hub
                        </span>
                      </button>
                      <Link to="/settings" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2814" data-mp="3s73rib3ededb">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2815" data-mp="tiep6tqx3jq1">
                          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" data-id="element-2816" data-mp="w2ujwr8ehldc"></path>
                          <circle cx="12" cy="12" r="3" data-id="element-2817" data-mp="1oqqqcnnrurnb"></circle>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2818" data-mp="1toijtm42eltm">
                          Settings
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2819" data-mp="22oyjekhot8t7"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2820" data-mp="2nguhdqq3lma">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2821" data-mp="3usb0bhvuf8v8">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2822" data-mp="7lilqlyayr8u"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2823" data-mp="may8lqupqcj9"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2824" data-mp="hcdb3z6ui6yi"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2825" data-mp="vc5wdezlruz3">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2826" data-mp="2s4o8c99w2xxb">
        {/* Header with Toggle */}
        <div className="mb-8" data-id="element-2827" data-mp="17m3z6t3cn0px">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-2828" data-mp="17mx7vzlvc0ap">
            <div data-id="element-2829" data-mp="1ckyswq2uu9ot">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2" data-id="element-2830" data-mp="1cnkv0a6z7wzn">
                User Management
              </h1>
              <p className="text-gray-600 text-sm sm:text-base" data-id="element-2831" data-mp="12vf473sgnlm1">
                Manage learners across your platform
              </p>
            </div>

            {/* Add Admin Button */}
            <div className="flex items-center self-start" data-id="element-2832" data-mp="tupemhtsmrgr">
              <button onClick={() => setShowAddAdminModal(true)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors bg-white shadow-sm" data-id="element-2833" data-mp="twa8e58aa6dk">
                <Shield size={16} data-id="element-2834" data-mp="2a98rev3fmfb6" />
                Add Admin
              </button>
            </div>
          </div>
        </div>

        <section aria-labelledby="learner-workflow-title" className="mb-8 rounded-xl border border-[#30718D]/15 bg-[#EAF4F7] px-4 py-4 sm:px-6" data-id="element-2835" data-mp="2eb3rjw9mmmnd">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-2836" data-mp="1pi3j4btvq7q3">
            <div data-id="element-2837" data-mp="1kj0egshta3in">
              <h2 id="learner-workflow-title" className="text-sm font-bold text-[#001823]" data-id="element-2838" data-mp="1fm08siovj2s2">A simple learner-management flow</h2>
              <p className="mt-1 text-sm text-[#365866]" data-id="element-2839" data-mp="1anozc92hquad">Add learners, find them in the table, then review capacity before assigning skills or pathways.</p>
            </div>
            <ol className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#236B8C]" aria-label="Learner management steps" data-id="element-2840" data-mp="11ulpsbuyv1l0">
              <li className="rounded-full bg-white px-3 py-1.5 shadow-sm" data-id="element-2841" data-mp="wwk5xoppuy8h">1. Add learners</li>
              <li aria-hidden="true" className="hidden text-[#5593B1] sm:block" data-id="element-2842" data-mp="rxh0i35tqpzz">→</li>
              <li className="rounded-full bg-white px-3 py-1.5 shadow-sm" data-id="element-2843" data-mp="mxuq01hdrnuv">2. Review learners</li>
              <li aria-hidden="true" className="hidden text-[#5593B1] sm:block" data-id="element-2844" data-mp="iv7spl1xxaqa">→</li>
              <li className="rounded-full bg-white px-3 py-1.5 shadow-sm" data-id="element-2845" data-mp="2ujcw5t1806d0">3. Assign learning</li>
            </ol>
          </div>
        </section>

        {/* Quick Actions */}
        <div data-phoebe-tour="learners" className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8" data-id="element-2846" data-mp="2ymj0gv4rvngu">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} onClick={() => setShowAddModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2847" data-mp="33hy8v7k0gr5v">
            <div className="flex items-center gap-4" data-id="element-2848" data-mp="2ks8gss7mujhn">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#30718D] to-[#265a70] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2849" data-mp="2pjzy0c4wfcv4">
                <UserPlus size={24} className="text-white" data-id="element-2850" data-mp="1s2i2ai239fl0" />
              </div>
              <div className="flex-1" data-id="element-2851" data-mp="1x24f4qe08tta">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-2852" data-mp="1p2zsmfbas0zh">
                  Add Individual Learner
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2853" data-mp="1kzbjhs70oxc4">
                  Register a single learner
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} onClick={() => setShowBulkModal(true)} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2854" data-mp="1tv12xvownhmv">
            <div className="flex items-center gap-4" data-id="element-2855" data-mp="1b389k05xbuem">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#007558] to-[#005a44] flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-2856" data-mp="1b1nd2501u1e4">
                <Upload size={24} className="text-white" data-id="element-2857" data-mp="2mhbz8u5hawdv" />
              </div>
              <div className="flex-1" data-id="element-2858" data-mp="8a4dhpmjye44">
                <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#007558] transition-colors" data-id="element-2859" data-mp="892pkkkftx4z">
                  Bulk Upload Learners
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2860" data-mp="3vg3r617tf0ga">
                  Import multiple learners via CSV
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* User List */}
        <div data-phoebe-tour="learner-list" className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-2861" data-mp="rz51nbllujuf">
          <div className="border-b border-gray-200 px-4 py-5 sm:px-6" data-id="element-2862" data-mp="w21cuh8tfdoc">
            <div className="mb-5 flex items-center justify-between" data-id="element-2863" data-mp="i8adu2spnc6v">
              <div className="flex items-center gap-2" data-id="element-2864" data-mp="n73f8wuq3cqo">
                <h2 className="flex items-center gap-2 text-base font-bold text-gray-900" data-id="element-2865" data-mp="n5jyz692h1ow">
                  <Users size={22} className="text-[#30718D]" aria-hidden="true" data-id="element-2866" data-mp="1avedds91ayb9" />
                  All Learners ({filteredUsers.length})
                </h2>
                <AIInsightPopover headline="A focused follow-up could activate more learners" insightBeforeMetric="There are" highlightedMetric="4 learners" insightAfterMetric=" who are pending or have not started." explanation="A targeted reminder with a clear first step can help move these learners into active training." briefing={{
                metrics: [{
                  label: 'Learners needing follow-up',
                  value: '4'
                }, {
                  label: 'Pending registration',
                  value: '2'
                }, {
                  label: 'Not started',
                  value: '2'
                }],
                signals: ['Emily Davis and Priya Nair are pending account registration.', 'David Park and Marcus Wilson have not started their assigned learning.'],
                actions: ['Show the 4 learners', 'Draft a reminder for pending learners', 'Plan outreach for learners who have not started'],
                actionResponses: {
                  'Show the 4 learners': '**Learners needing follow-up**\n\n• Emily Davis — Pending registration\n• Priya Nair — Pending registration\n• David Park — Not started\n• Marcus Wilson — Not started\n\nStart with the two pending learners by resending their account-creation email, then send a focused learning reminder to David and Marcus.'
                }
              }} align="left" data-id="element-2867" data-mp="3bsdssnis8fq6" />
              </div>
            </div>

            {/* Search and Filter */}
            <div className="flex flex-col items-stretch gap-3 xl:flex-row xl:items-center xl:gap-4" data-id="element-2868" data-mp="tt4b6ussei0f">
              <div className="relative w-full xl:flex-1" data-id="element-2869" data-mp="yle7cskhweb5">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5E7079]" data-id="element-2870" data-mp="13jz6eoo0gry0" />
                <input type="text" placeholder="Search name or email" value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }} className="w-full rounded-lg border border-gray-300 py-2.5 pl-11 pr-4 text-sm text-gray-900 placeholder:text-[#5E7079] focus:border-[#30718D] focus:outline-none focus:ring-2 focus:ring-[#30718D]/20" data-id="element-2871" data-mp="18iai74xv9civ" />
              </div>
              {/* Mobile: Custom Dropdown */}
              <div className="sm:hidden w-full relative" ref={mobileFilterRef} data-id="element-2872" data-mp="3jcoe0ng4p4y0">
                <button onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2873" data-mp="3v0hd2j2rfhw3">
                  <div className="flex items-center gap-2" data-id="element-2874" data-mp="dqjd45vmjx9j">
                    <Filter size={16} className="text-gray-400" data-id="element-2875" data-mp="3cbas0pehue74" />
                    <span data-id="element-2876" data-mp="3h92x6l45fwt9">
                      {selectedFilter === 'all' ? 'All' : selectedFilter === 'not_assigned' ? 'Not Assigned' : selectedFilter === 'not_started' ? 'Not Started' : selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)}
                    </span>
                  </div>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isMobileFilterOpen ? 'rotate-180' : ''}`} data-id="element-2877" data-mp="2tk02ocof29hc" />
                </button>
                {isMobileFilterOpen && <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-2878" data-mp="2ygia5kb4slxd">
                    {[{
                  value: 'all',
                  label: 'All'
                }, {
                  value: 'pending',
                  label: 'Pending'
                }, {
                  value: 'not_assigned',
                  label: 'Not Assigned'
                }, {
                  value: 'active',
                  label: 'Active'
                }, {
                  value: 'inactive',
                  label: 'Inactive'
                }, {
                  value: 'not_started',
                  label: 'Not Started'
                }, {
                  value: 'completed',
                  label: 'Completed'
                }].map((filter, idx) => <button key={filter.value} onClick={() => {
                  setSelectedFilter(filter.value);
                  setCurrentPage(1);
                  setIsMobileFilterOpen(false);
                }} className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${idx < 6 ? 'border-b border-gray-100' : ''} ${selectedFilter === filter.value ? 'bg-[#30718D]/5 text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2879" data-mp="dy3iinznai65" data-mpi={idx}>
                        {filter.label}
                      </button>)}
                  </div>}
              </div>
              {/* Desktop: Pill buttons */}
              <div className="hidden self-end sm:flex items-center gap-1.5 rounded-lg bg-gray-100 p-1 xl:ml-auto xl:self-auto" data-id="element-2880" data-mp="i2fcwvrnpacg">
                {[{
                value: 'all',
                label: 'All'
              }, {
                value: 'pending',
                label: 'Pending'
              }, {
                value: 'not_assigned',
                label: 'Not Assigned'
              }, {
                value: 'active',
                label: 'Active'
              }, {
                value: 'inactive',
                label: 'Inactive'
              }, {
                value: 'not_started',
                label: 'Not Started'
              }, {
                value: 'completed',
                label: 'Completed'
              }].map(filter => <button key={filter.value} onClick={() => {
                setSelectedFilter(filter.value);
                setCurrentPage(1);
              }} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${selectedFilter === filter.value ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2881" data-mp="2lgzlu8xf1fdz">
                    {filter.label}
                  </button>)}
              </div>
            </div>
            {paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.includes(user.id)) && <div className="mt-4 flex flex-col gap-2 rounded-lg border border-[#30718D]/20 bg-[#30718D]/5 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-id="element-2882" data-mp="2chchpfdaxvh8">
                {selectedUsers.length === filteredUsers.length ? <>
                    <p className="font-medium text-[#236B8C]" data-id="element-2883" data-mp="3nzvpw0he8jci">All {filteredUsers.length} learners in this table are selected.</p>
                    <button type="button" onClick={handleDeselectAllFilteredUsers} className="w-fit font-semibold text-[#30718D] underline decoration-1 underline-offset-4 hover:text-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2884" data-mp="22f0en1cey3w6">Deselect all</button>
                  </> : <>
                    <p className="text-gray-700" data-id="element-2885" data-mp="2gg331kqvrqa9">All {paginatedUsers.length} learners on this page are selected.</p>
                    <button type="button" onClick={handleSelectAllFilteredUsers} className="w-fit font-semibold text-[#30718D] underline decoration-1 underline-offset-4 hover:text-[#265A70] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2886" data-mp="2ld39vxvk0x2f">Select all {filteredUsers.length} learners</button>
                  </>}
              </div>}
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto" data-id="element-2887" data-mp="345nn66yrstl4">
            <table className="w-full min-w-[1080px] text-sm" data-id="element-2888" data-mp="342jf2yi6pdp6">
              <thead data-id="element-2889" data-mp="3eaduytguurx9">
                <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2890" data-mp="3e6pzvpvrhxgf">
                  <th className="w-8 px-4 py-3 sm:w-12 sm:px-6" data-id="element-2891" data-mp="398yab2ilanfe">
                    <input type="checkbox" aria-label="Select all learners on this page" checked={paginatedUsers.length > 0 && paginatedUsers.every(user => selectedUsers.includes(user.id))} onChange={handleSelectAll} className={`h-4 w-4 rounded border-gray-300 transition-colors ${noLicensesAvailable ? 'cursor-pointer opacity-40 accent-gray-400' : 'cursor-pointer text-[#30718D] focus:ring-[#30718D]'}`} data-id="element-2892" data-mp="34a3gz6mzxgh1" />
                  </th>
                  <th className={`px-4 py-3 text-left whitespace-nowrap transition-colors sm:px-6 ${headerActiveClass('name')}`} data-id="element-2893" data-mp="3n8693uaubtj2">
                    <button type="button" onClick={() => handleSort('name')} className={headerButtonClass('name')} data-id="element-2894" data-mp="3j5t4xabqs3oe">
                      <span data-id="element-2895" data-mp="etpclju6b7ru">Name</span>
                      <SortArrow columnKey="name" data-id="element-2896" data-mp="esmjr6daiv9a" />
                    </button>
                  </th>
                  <th className={`px-4 py-3 text-left whitespace-nowrap transition-colors sm:px-6 ${headerActiveClass('status')}`} data-id="element-2897" data-mp="292kq2nob7g6n">
                    <button type="button" onClick={() => handleSort('status')} className={headerButtonClass('status')} data-id="element-2898" data-mp="2e2p6yet4qcxe">
                      Status
                      <SortArrow columnKey="status" data-id="element-2899" data-mp="24a1chqpmgl1u" />
                    </button>
                  </th>
                  <th className={`hidden px-4 py-3 text-left whitespace-nowrap transition-colors sm:table-cell sm:px-6 ${headerActiveClass('skills')}`} data-id="element-2900" data-mp="1qas59gje91a0">
                    <button type="button" onClick={() => handleSort('skills')} className={headerButtonClass('skills')} data-id="element-2901" data-mp="1v7s2r326gvpi">
                      Skill Modules
                      <SortArrow columnKey="skills" data-id="element-2902" data-mp="1lcqd863zprme" />
                    </button>
                  </th>
                  <th className={`hidden px-4 py-3 text-left whitespace-nowrap transition-colors sm:table-cell sm:px-6 ${headerActiveClass('lastActive')}`} data-id="element-2903" data-mp="3as6k1eufz194">
                    <button type="button" onClick={() => handleSort('lastActive')} className={headerButtonClass('lastActive')} data-id="element-2904" data-mp="142czs87cjsha">
                      Last Active
                      <SortArrow columnKey="lastActive" data-id="element-2905" data-mp="z5crrrqxsfnj" />
                    </button>
                  </th>
                  <th className="w-12 px-4 py-3 text-center text-sm font-semibold text-gray-700 sm:w-auto sm:px-6" data-id="element-2906" data-mp="1duis5j1igbx0">
                    <span className="hidden sm:inline" data-id="element-2907" data-mp="1mt44gwge2upc">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-2908" data-mp="1isalmhht063b">
                {paginatedUsers.map(user => <Fragment key={`${userType}-${user.id}`} data-id="element-2909" data-mp="1itvdfzcqhaxh">
                      <tr onClick={e => {
                  const target = e.target as HTMLElement;
                  if (!target.closest('input[type="checkbox"]') && !target.closest('button')) {
                    setExpandedUserId(expandedUserId === user.id ? null : user.id);
                  }
                }} className={`cursor-pointer border-b border-gray-100 transition-colors last:border-b-0 ${expandedUserId === user.id ? 'bg-[#30718D]/5 ring-1 ring-inset ring-[#30718D]/15' : 'hover:bg-gray-50'}`} data-id="element-2910" data-mp="1wpz5bm04x76s">
                        <td className="align-middle px-4 py-4 sm:px-6" data-id="element-2911" data-mp="3cnemu4nmmn33">
                          <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => handleSelectUser(user.id)} className={`h-4 w-4 rounded border-gray-300 transition-colors ${noLicensesAvailable ? 'cursor-pointer opacity-40 accent-gray-400' : 'cursor-pointer text-[#30718D] focus:ring-[#30718D]'}`} data-id="element-2912" data-mp="3hhitemszct3n" />
                        </td>
                        <td className={`align-middle px-4 py-4 font-medium text-gray-900 transition-colors sm:px-6 ${cellActiveClass('name')}`} data-id="element-2913" data-mp="2ypq2qz1dvjz3">
                          <div className="min-w-0" data-id="element-2914" data-mp="33pkqwutrxvgg">
                            <div className="truncate text-sm font-medium text-gray-900" data-id="element-2915" data-mp="dzhe5sl6og3g">
                              {user.name}
                            </div>
                            <div className="truncate text-sm text-gray-700" data-id="element-2916" data-mp="iwr4dagecvsc">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className={`align-middle px-4 py-4 transition-colors sm:px-6 ${cellActiveClass('status')}`} data-id="element-2917" data-mp="1yjmj11pry2aa">
                          <div className="relative group/status inline-flex" data-id="element-2918" data-mp="1os01yoqr98zm">
                            <span className={`inline-flex h-6 items-center justify-center rounded border bg-white px-3 text-xs font-bold whitespace-nowrap cursor-help ${user.status === 'active' ? 'border-[#5290A6] text-[#30718D]' : user.status === 'completed' ? 'border-[#05634B] text-[#05634B]' : user.status === 'pending' ? 'border-[#BFAF00] text-[#A16207]' : user.status === 'not_started' ? 'border-[#7E22CE] text-[#7E22CE]' : user.status === 'not_assigned' ? 'border-[#DB3725] text-[#DB3725]' : user.status === 'inactive' ? 'border-[#828282] text-[#6B7280]' : 'border-gray-300 text-gray-600'}`} data-id="element-2919" data-mp="1opw0edgbwm2i">
                              {user.status === 'not_assigned' ? 'Not Assigned' : user.status === 'not_started' ? 'Not Started' : user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                            </span>
                            <div className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-56 -translate-x-1/2 invisible rounded-lg bg-gray-900 px-3 py-2 text-center text-xs text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/status:visible group-hover/status:opacity-100" data-id="element-2920" data-mp="40ygtzcifbdq">
                              {user.status === 'not_assigned' && 'This learner has no skill modules or Pathways assigned to them.'}
                              {user.status === 'pending' && 'This learner has not completed their account registration. Send them a reminder email.'}
                              {user.status === 'not_started' && 'This learner has not started any skill modules or Pathways. Send them a reminder email.'}
                              {user.status === 'active' && 'This learner has accessed a skill module in the past 30 days.'}
                              {user.status === 'inactive' && 'This learner has not accessed a skill module in the past 30 days. Send them a reminder email.'}
                              {user.status === 'completed' && 'This learner completed all available skill modules and Pathways.'}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900" data-id="element-2921" data-mp="ubgyxzmyputb" />
                            </div>
                          </div>
                        </td>
                        <td className={`hidden align-middle px-4 py-4 text-gray-700 tabular-nums transition-colors sm:table-cell sm:px-6 ${cellActiveClass('skills')}`} data-id="element-2922" data-mp="18b9rm0i1ul3x">
                          {`${'skills' in user ? user.skills : 0} skills`}
                        </td>
                        <td className={`hidden align-middle px-4 py-4 text-gray-700 whitespace-nowrap transition-colors sm:table-cell sm:px-6 ${cellActiveClass('lastActive')}`} data-id="element-2923" data-mp="20xufus38ah39">
                          {user.lastActive}
                        </td>
                        <td className="align-middle px-4 py-4 sm:px-6" data-id="element-2924" data-mp="2aocidqn2at3u">
                          <div className="flex items-center justify-center" data-id="element-2925" data-mp="26lfvvoueicum">
                            <div className="relative" ref={el => actionMenuRefs.current[user.id] = el} data-id="element-2926" data-mp="26niu2w5pjcaf">
                              <button onClick={() => setOpenActionMenuId(openActionMenuId === user.id ? null : user.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-2927" data-mp="1wni4ish8vzxs">
                                <MoreVertical size={16} data-id="element-2928" data-mp="1mxow20ceg0xr" />
                              </button>

                              <AnimatePresence data-id="element-2929" data-mp="1iw3n2q2zk6su">
                                {openActionMenuId === user.id && <motion.div initial={{
                            opacity: 0,
                            scale: 0.95,
                            y: -10
                          }} animate={{
                            opacity: 1,
                            scale: 1,
                            y: 0
                          }} exit={{
                            opacity: 0,
                            scale: 0.95,
                            y: -10
                          }} transition={{
                            duration: 0.15
                          }} className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden" data-id="element-2930" data-mp="1iu0o3gjuv3bz">
                                    <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm border-b border-gray-100" data-id="element-2931" data-mp="36tcsvkb4pd0e">
                                      <Users size={16} className="text-gray-600" data-id="element-2932" data-mp="3fn7zkwrwfbi5" />
                                      <span className="font-medium text-gray-900" data-id="element-2933" data-mp="2j4tmjabn3aay">
                                        View Learner
                                      </span>
                                    </button>
                                    <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2934" data-mp="2421wg53ydgoe">
                                      <Mail size={16} className="text-gray-600" data-id="element-2935" data-mp="2dr3c1u6l5w1u" />
                                      <span className="font-medium text-gray-900" data-id="element-2936" data-mp="2dt67wuuf6jeh">
                                        Email
                                      </span>
                                    </button>
                                  </motion.div>}
                              </AnimatePresence>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row Details */}
                      <AnimatePresence data-id="element-2937" data-mp="2rre6f773tsrr">
                        {expandedUserId === user.id && <motion.tr initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} data-id="element-2938" data-mp="12cy5ctezn82t">
                            <td colSpan={6} className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6" data-id="element-2939" data-mp="3dxyo8tghwvcp">
                              <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.3
                      }} className="overflow-hidden" data-id="element-2940" data-mp="2z9uc68by7i2w">
                                <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-2941" data-mp="2lbdzm0remxcu">
                                  {/* Assigned Pathways */}
                                  <div className="pb-4 border-b border-gray-200" data-id="element-2942" data-mp="jizgwzh1dovd">
                                    <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-2943" data-mp="i22h3j0vn9r2">
                                      Assigned Pathways{' '}
                                      <span className="uppercase" data-id="element-2944" data-mp="d26dvm007mck">
                                        (
                                        {user.assignedPathways && user.assignedPathways.length > 0 ? `${user.assignedPathways.reduce((sum: number, name: string) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0)} SKILLS` : '0 SKILLS'}
                                        )
                                      </span>
                                    </p>
                                    {user.assignedPathways && user.assignedPathways.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2945" data-mp="m3npar2guzux">
                                        {user.assignedPathways.map((pathway, idx) => <span key={idx} className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-purple-50 rounded text-xs font-medium text-purple-700" data-id="element-2946" data-mp="1jjbcy7f3szid" data-mpi={idx}>
                                              <span className="text-[10px] font-bold uppercase tracking-wide" data-id="element-2947" data-mp="1n0z4s204rvqw">
                                                Pathway
                                              </span>
                                              {pathway}
                                            </span>)}
                                      </div> : <p className="text-sm text-gray-400 italic" data-id="element-2948" data-mp="211rywlmisoth">
                                        No pathways assigned
                                      </p>}
                                  </div>

                                  {/* Assigned Skills */}
                                  <div className="pt-4" data-id="element-2949" data-mp="2ern2t56icl0c">
                                    <p className="text-xs font-medium text-gray-500 mb-3" data-id="element-2950" data-mp="2eu867ymgf907">
                                      Assigned Skills{' '}
                                      <span className="uppercase" data-id="element-2951" data-mp="2or46jh278oto">
                                        (
                                        {user.assignedSkills ? user.assignedSkills.length : 0}{' '}
                                        SKILLS)
                                      </span>
                                    </p>
                                    {user.assignedSkills && user.assignedSkills.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2952" data-mp="3o1asuibz39je">
                                        {user.assignedSkills.map((skill, idx) => <span key={idx} className="px-2.5 py-1.5 bg-[#30718D]/10 rounded text-xs font-medium text-[#30718D]" data-id="element-2953" data-mp="3j397fqr2qy4r" data-mpi={idx}>
                                              {skill}
                                            </span>)}
                                      </div> : <p className="text-sm text-gray-400 italic" data-id="element-2954" data-mp="351wt1jrkb4kz">
                                        No skills assigned
                                      </p>}
                                  </div>
                                </div>
                              </motion.div>
                            </td>
                          </motion.tr>}
                      </AnimatePresence>
                    </Fragment>)}
              </tbody>
            </table>

            {/* Pagination */}
            {filteredUsers.length > 0 && totalPages > 1 && <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-gray-200" data-id="element-2955" data-mp="rx1iiqpld2fq">
                <p className="text-sm text-gray-600" data-id="element-2956" data-mp="e5woq58wn4db">
                  Showing {(safeCurrentPage - 1) * itemsPerPage + 1}–
                  {Math.min(safeCurrentPage * itemsPerPage, filteredUsers.length)}{' '}
                  of {filteredUsers.length}
                </p>
                <div className="flex items-center gap-2" data-id="element-2957" data-mp="1at1gqj95x27r">
                  <span className="text-sm text-gray-600" data-id="element-2958" data-mp="wutkyenv46y6">Page</span>
                  <div className="relative" ref={paginationDropdownRef} data-id="element-2959" data-mp="wwnvr05f6ioj">
                    <button type="button" onClick={() => setIsPaginationDropdownOpen(open => !open)} aria-haspopup="listbox" aria-expanded={isPaginationDropdownOpen} aria-label="Select page" className={`flex min-w-14 items-center justify-between rounded-md border bg-white py-1 pl-2 pr-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D]/20 ${isPaginationDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20 text-[#30718D]' : 'border-gray-300 text-gray-700'}`} data-id="element-2960" data-mp="11s38foroy8jd">
                      <span data-id="element-2961" data-mp="3nsbb9lvptikb">{safeCurrentPage}</span>
                      <ChevronDown size={14} aria-hidden="true" className={`ml-2 text-gray-400 transition-transform ${isPaginationDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2962" data-mp="3rtn37y22afvo" />
                    </button>
                    {isPaginationDropdownOpen && <div role="listbox" aria-label="Select page" className="absolute bottom-full z-30 mb-1 max-h-60 min-w-full overflow-y-auto overflow-x-hidden rounded-lg border border-gray-200 bg-white shadow-lg" data-id="element-2963" data-mp="3iujh0l7llkcy">
                        {Array.from({
                    length: totalPages
                  }, (_, i) => i + 1).map(page => <button key={page} type="button" role="option" aria-selected={safeCurrentPage === page} onClick={() => {
                    setCurrentPage(page);
                    setIsPaginationDropdownOpen(false);
                  }} className={`flex w-full items-center border-b border-gray-100 px-3 py-2 text-left text-sm transition-colors last:border-b-0 ${safeCurrentPage === page ? 'bg-[#30718D]/10 font-medium text-[#30718D]' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2964" data-mp="2v4p6iaar47vv">
                          {page}
                        </button>)}
                      </div>}
                  </div>
                  <span className="text-sm text-gray-600" data-id="element-2965" data-mp="2zdzryzb27xzk">of {totalPages}</span>
                </div>
              </div>}
          </div>

          {filteredUsers.length === 0 && <div className="p-12 text-center" data-id="element-2966" data-mp="394kq6lmtn0hy">
              <Users size={48} className="mx-auto text-gray-400 mb-4" data-id="element-2967" data-mp="346sz1tu03iet" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2968" data-mp="347ktv2frxc41">
                No users found
              </h3>
              <p className="text-gray-600" data-id="element-2969" data-mp="3i47qf43te09f">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>

        <SkillsOverview data-id="element-2970" data-mp="2avcpy248dcaa" />
      </div>

      {/* Add Admin Modal */}
      <AnimatePresence data-id="element-2971" data-mp="2fsmoc79lkya9">
        {showAddAdminModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={handleCloseAdminModal} data-id="element-2972" data-mp="2fto3amtqi4x9">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-2973" data-mp="2olg92k7tcpvi">
              <div className="flex items-center justify-between mb-6" data-id="element-2974" data-mp="37j0xxrakvog4">
                <div className="flex items-center gap-3" data-id="element-2975" data-mp="1957kod2ye12r">
                  <div className="w-10 h-10 rounded-full bg-[#30718D]/10 flex items-center justify-center" data-id="element-2976" data-mp="196skah44ka7h">
                    <Shield size={20} className="text-[#30718D]" data-id="element-2977" data-mp="1d848qjb9ipe3" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2978" data-mp="15ouxsln3yimx">
                    {adminModalStep === 'form' ? 'Add Admin' : 'Admin Added'}
                  </h2>
                </div>
                <button onClick={handleCloseAdminModal} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-2979" data-mp="vx8k8inas7h0">
                  Close
                </button>
              </div>

              {adminModalStep === 'form' ? <>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-2980" data-mp="1jnup61209bms">
                    Provide the following information to add an additional
                    administrator account.
                  </p>

                  <div className="space-y-6 mb-6" data-id="element-2981" data-mp="1al3c5wthe7t1">
                    {adminEntries.map((entry, index) => <div key={index} className="space-y-4" data-id="element-2982" data-mp="46u7wquxn37g" data-mpi={index}>
                        <h3 className="text-sm font-bold text-gray-900" data-id="element-2983" data-mp="3vc3su442kra7">
                          Admin {index + 1}
                        </h3>
                        <div className="grid grid-cols-2 gap-4" data-id="element-2984" data-mp="1n2m884cxmilr">
                          <div data-id="element-2985" data-mp="1runqq80p15k8">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2986" data-mp="1ruw3lzwtgavf">
                              First Name{' '}
                              <span className="text-[#DB3725]" data-id="element-2987" data-mp="1e0wgsyj1vjnz">*</span>
                            </label>
                            <input type="text" placeholder="Enter first name" value={entry.firstName} onChange={e => handleAdminEntryChange(index, 'firstName', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2988" data-mp="25tvt19s5ktsn" />
                          </div>
                          <div data-id="element-2989" data-mp="21qz471c0s1g9">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2990" data-mp="21ueja45p1aho">
                              Last Name{' '}
                              <span className="text-[#DB3725]" data-id="element-2991" data-mp="qdodun93gmp4">*</span>
                            </label>
                            <input type="text" placeholder="Enter last name" value={entry.lastName} onChange={e => handleAdminEntryChange(index, 'lastName', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2992" data-mp="hvcw6abkrfny" />
                          </div>
                        </div>
                        <div data-id="element-2993" data-mp="3varbmzl2szhq">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2994" data-mp="3vc2ft0ifo99d">
                            Email <span className="text-[#DB3725]" data-id="element-2995" data-mp="3qcz8taiw7syr">*</span>
                          </label>
                          <input type="email" placeholder="Enter email address" value={entry.email} onChange={e => handleAdminEntryChange(index, 'email', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" data-id="element-2996" data-mp="8whnmrl25thh" />
                        </div>
                      </div>)}
                  </div>

                  <div className="mb-6" data-id="element-2997" data-mp="1bh8tbwr4aqep">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-2998" data-mp="1bg7fjkipvpta">
                      <input type="checkbox" checked={adminAcknowledge} onChange={e => setAdminAcknowledge(e.target.checked)} className="w-4 h-4 mt-0.5 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-2999" data-mp="1gftubye71e47" />
                      <span className="text-sm text-gray-700 leading-snug" data-id="element-3000" data-mp="1pbt0ngltxpgf">
                        <span className="text-[#DB3725]" data-id="element-3001" data-mp="sl0jefn5trd2">*</span> I acknowledge
                        that I have the authority to share the information of
                        the learners I provided.
                      </span>
                    </label>
                  </div>

                  <div className="flex gap-3 mb-6" data-id="element-3002" data-mp="16k9x98nw89dv">
                    <button onClick={handleAddAdminEntry} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-3003" data-mp="2czaxvrnv5fqs">
                      Add another
                    </button>
                    <button onClick={handleAdminSubmit} disabled={!adminAcknowledge || !adminEntries.every(e => e.firstName && e.lastName && e.email)} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${adminAcknowledge && adminEntries.every(e => e.firstName && e.lastName && e.email) ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3004" data-mp="2s19c5hhqhtoc">
                      Submit
                    </button>
                  </div>

                  <div className="space-y-3 border-t border-gray-200 pt-4" data-id="element-3005" data-mp="24boqgqu74ibd">
                    <p className="text-gray-500 text-xs leading-relaxed" data-id="element-3006" data-mp="1zd3p2o37jslc">
                      New admins will have all the same capabilities you
                      currently have, including registering employees for
                      training, monitoring learning progress, adding additional
                      administrator accounts and purchasing¹ more training to be
                      distributed through this License Management interface.
                    </p>
                    <p className="text-gray-400 text-xs" data-id="element-3007" data-mp="gv0imkwhdgns">
                      ¹ Payment information is never saved or stored and all
                      admins will have to enter new payment information when
                      purchasing additional training content.
                    </p>
                  </div>
                </> : <div className="py-8 text-center flex flex-col items-center" data-id="element-3008" data-mp="3pak9jd9oimzo">
                  <div className="w-16 h-16 bg-[#007558]/10 rounded-full flex items-center justify-center mb-6" data-id="element-3009" data-mp="3u9ojw78caqrl">
                    <CheckCircle size={32} className="text-[#007558]" data-id="element-3010" data-mp="3gaox754e0ly2" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3011" data-mp="3l8qdxjecc96d">
                    You've added an additional admin
                  </h3>
                  <p className="text-gray-600 text-sm max-w-sm mx-auto" data-id="element-3012" data-mp="1ejk382ezf9ak">
                    We'll send an email to the address you provided with more
                    information on how to create a B2B admin account.
                  </p>
                </div>}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Add Individual User Modal */}
      <AnimatePresence data-id="element-3013" data-mp="22edu570ngl7x">
        {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => {
        setShowAddModal(false);
      }} data-id="element-3014" data-mp="22g8ccdkjdz8b">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-3015" data-mp="2cb09xhe4cm33">
              <div className="flex items-center justify-between mb-6" data-id="element-3016" data-mp="qzgwj8qegnoy">
                <div className="flex items-center gap-3" data-id="element-3017" data-mp="myez8xg1mlpm">
                  <div className="w-10 h-10 rounded-full bg-[#30718D]/10 flex items-center justify-center" data-id="element-3018" data-mp="3s0yik6456w8n">
                    <UserPlus size={20} className="text-[#30718D]" data-id="element-3019" data-mp="3n2552douv58h" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-3020" data-mp="3ib5672w6ed3c">
                    {addModalStep === 'form' ? `Add Learner` : 'Confirm Registration'}
                  </h2>
                </div>
                <button onClick={() => {
              setShowAddModal(false);
              setIsLicenseDropdownOpen(false);
              setLicenseSearch('');
              setAddModalStep('form');
              setAcknowledgeChecked(false);
              setSelectedModalPathways([]);
              setSelectedLicenses([]);
            }} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-3021" data-mp="39aguwfy62qu5">
                  Close
                </button>
              </div>

              {addModalStep === 'form' ? <>
                  <div className="space-y-4 mb-6" data-id="element-3022" data-mp="smconc2ysx5u">
                    <div data-id="element-3023" data-mp="1133hmg0upfh2">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3024" data-mp="111shcq6m6pup">
                        First Name <span className="text-[#DB3725]" data-id="element-3025" data-mp="153dorw0dqbxi">*</span>
                      </label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter first name" data-id="element-3026" data-mp="19uvsusxp8smp" />
                    </div>
                    <div data-id="element-3027" data-mp="m6m9ryrveiz9">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3028" data-mp="m5uophvwg3n8">
                        Last Name <span className="text-[#DB3725]" data-id="element-3029" data-mp="r32zuyimr7ez">*</span>
                      </label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter last name" data-id="element-3030" data-mp="w5br9qfuu1ez" />
                    </div>
                    <div data-id="element-3031" data-mp="1ht76qpl6b5pe">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3032" data-mp="1hwl8pwpxdcwb">
                        Email Address <span className="text-[#DB3725]" data-id="element-3033" data-mp="1cy1j9mc4hiz7">*</span>
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent" placeholder="Enter email address" data-id="element-3034" data-mp="1rqlmemz9vqgn" />
                    </div>
                    <div data-id="element-3035" data-mp="u3cti6wxzf4m">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3036" data-mp="18ttxny0yoj57">
                        Licenses
                      </label>
                      <div className="relative" ref={licenseDropdownRef} data-id="element-3037" data-mp="1422dq6cb9bo1">
                        <div className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between cursor-text ${isLicenseDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]' : 'border-gray-300'}`} onClick={() => setIsLicenseDropdownOpen(true)} data-id="element-3038" data-mp="gcrb0jbeh779">
                          <input type="text" placeholder={selectedLicenses.length + selectedModalPathways.length > 0 ? `${selectedLicenses.length + selectedModalPathways.length} item${selectedLicenses.length + selectedModalPathways.length > 1 ? 's' : ''} selected — type to search...` : 'Search and select pathways or modules'} value={licenseSearch} onChange={e => {
                      setLicenseSearch(e.target.value);
                      if (!isLicenseDropdownOpen) setIsLicenseDropdownOpen(true);
                    }} onFocus={() => setIsLicenseDropdownOpen(true)} className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" data-id="element-3039" data-mp="2rsi2gqm131n9" />
                          <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isLicenseDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3040" data-mp="30rmxvo2amev7" />
                        </div>
                        {isLicenseDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden" data-id="element-3041" data-mp="1pbyaivl4ndtv">
                            <div className="max-h-72 overflow-y-auto" data-id="element-3042" data-mp="20to3zmu4lrj1">
                              {/* Pathways Section */}
                              {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).length > 0 && <>
                                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3043" data-mp="2ok9xm93dv99i">
                                    <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-3044" data-mp="2jkxadn8lo06e">
                                      Pathways
                                    </span>
                                    <button type="button" onClick={e => {
                            e.stopPropagation();
                            selectAllModalPathways();
                          }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-3045" data-mp="2ajh2bm59ca5c">
                                      {selectedModalPathways.length === pathwayData.length ? 'Deselect all' : 'Select all'}
                                    </button>
                                  </div>
                                  {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-3046" data-mp="3ol5d526r1gcn">
                                        <input type="checkbox" checked={selectedModalPathways.includes(pathway.name)} onChange={() => toggleModalPathway(pathway.name)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-3047" data-mp="2cnave0pq7bk" />
                                        <div data-id="element-3048" data-mp="bd1zam8z42ja">
                                          <div className="text-sm font-semibold text-gray-900" data-id="element-3049" data-mp="2863s0tx6bnty">
                                            {pathway.name}
                                          </div>
                                          <div className="text-xs text-gray-500" data-id="element-3050" data-mp="3gfkr6z155bkp">
                                            {pathway.skillCount} skills
                                          </div>
                                        </div>
                                      </label>)}
                                </>}

                              {/* Individual Skills Section */}
                              {availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).length > 0 && <>
                                  <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3051" data-mp="3uekjqhrryam5">
                                    <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-3052" data-mp="c3qouw4mamtu">
                                      Individual Skills
                                    </span>
                                    <button type="button" onClick={e => {
                            e.stopPropagation();
                            selectAllModalSkills();
                          }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-3053" data-mp="lxhazud9ul6d">
                                      {selectedLicenses.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                                    </button>
                                  </div>
                                  {availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-3054" data-mp="hu6lf9bonj9c">
                                        <input type="checkbox" checked={selectedLicenses.includes(skill)} onChange={() => toggleLicense(skill)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-3055" data-mp="7zogy1yfzjoh" />
                                        <span className="text-sm text-gray-700" data-id="element-3056" data-mp="2yqzxsp64vok">
                                          {skill}
                                        </span>
                                      </label>)}
                                </>}

                              {pathwayData.filter(p => p.name.toLowerCase().includes(licenseSearch.toLowerCase())).length === 0 && availableSkills.filter(skill => skill.toLowerCase().includes(licenseSearch.toLowerCase())).length === 0 && <p className="px-4 py-3 text-sm text-gray-400" data-id="element-3057" data-mp="4gh6tj4wxihv">
                                    No results found
                                  </p>}
                            </div>
                          </div>}
                      </div>
                      {(selectedModalPathways.length > 0 || selectedLicenses.length > 0) && <div className="flex flex-wrap gap-1.5 mt-2 max-h-24 overflow-y-auto" data-id="element-3058" data-mp="3msnkx6xwtryy">
                          {selectedModalPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-3059" data-mp="3cx29ki6ybfcj">
                              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-3060" data-mp="38xl4hpvfsk7w">
                                Pathway
                              </span>
                              {pathway}
                              <button type="button" onClick={() => toggleModalPathway(pathway)} className="hover:text-[#DB3725] transition-colors" data-id="element-3061" data-mp="1xdl6mps4nuep">
                                ×
                              </button>
                            </span>)}
                          {selectedLicenses.map(skill => <span key={skill} className="inline-flex items-center gap-1 px-2 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-3062" data-mp="19o0kjy0ogh17">
                              {skill}
                              <button type="button" onClick={() => toggleLicense(skill)} className="hover:text-[#DB3725] transition-colors" data-id="element-3063" data-mp="1jdtnkamr7ntu">
                                ×
                              </button>
                            </span>)}
                        </div>}
                    </div>
                  </div>

                  <div className="flex gap-3" data-id="element-3064" data-mp="vswc41lva2qi">
                    <button onClick={() => {
                setShowAddModal(false);
                setIsLicenseDropdownOpen(false);
                setAddModalStep('form');
                setAcknowledgeChecked(false);
                setSelectedModalPathways([]);
                setSelectedLicenses([]);
              }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-3065" data-mp="vu7ebwznz2fi">
                      Cancel
                    </button>
                    <button onClick={() => {
                setIsLicenseDropdownOpen(false);
                setLicenseSearch('');
                setAddModalStep('confirm');
              }} disabled={!firstName.trim() || !lastName.trim() || !email.trim()} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${firstName.trim() && lastName.trim() && email.trim() ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3066" data-mp="1ogidr4bjaxaq">
                      Next
                    </button>
                  </div>
                </> : <>
                  {/* Confirmation Screen */}
                  <p className="text-sm text-gray-600 mb-4" data-id="element-3067" data-mp="3dyozcrq2w6pv">
                    Please ensure all learner information provided is accurate.
                  </p>

                  <div className="flex items-center gap-4 text-xs mb-4" data-id="element-3068" data-mp="agxb9of2c6wy">
                    {(() => {
                const totalPathwaySkills = selectedModalPathways.reduce((sum, name) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0);
                const totalSkills = totalPathwaySkills + selectedLicenses.length;
                return <>
                          <span className="text-gray-500" data-id="element-3069" data-mp="3rvauvw2h1jh4">
                            Licenses to be registered:{' '}
                            <span className="font-bold text-[#30718D]" data-id="element-3070" data-mp="ogopnq9hrpbh">
                              {totalSkills}
                            </span>
                          </span>
                          <span className="text-gray-300" data-id="element-3071" data-mp="2iqn11lzdb0fp">|</span>
                          <span className="text-gray-500" data-id="element-3072" data-mp="2mouarwvez1he">
                            Remaining:{' '}
                            <span className="font-bold text-gray-900" data-id="element-3073" data-mp="28y7m6xjdarz1">
                              {190 - totalSkills}
                            </span>
                          </span>
                        </>;
              })()}
                  </div>

                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-5" data-id="element-3074" data-mp="23ze9h7bwn4zx">
                    <div className="flex items-center gap-3 mb-3" data-id="element-3075" data-mp="1pzv2ihutxzp7">
                      <div className="w-10 h-10 rounded-full bg-[#30718D] flex items-center justify-center text-white text-sm font-bold" data-id="element-3076" data-mp="1q1polzhu7xuv">
                        {firstName.charAt(0).toUpperCase()}
                        {lastName.charAt(0).toUpperCase()}
                      </div>
                      <div data-id="element-3077" data-mp="3pcgzmwm2mwb3">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-3078" data-mp="3pammi4h1k8hk">
                          {firstName} {lastName}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-3079" data-mp="3uivxl8dv2k50">{email}</p>
                      </div>
                    </div>

                    {selectedModalPathways.length > 0 && <div className="pt-3 border-t border-gray-200" data-id="element-3080" data-mp="7dd9ogkug8f8">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-3081" data-mp="7b0hzbpaajoz">
                          Assigned Pathways (
                          {selectedModalPathways.reduce((sum, name) => sum + (pathwayData.find(p => p.name === name)?.skillCount || 0), 0)}{' '}
                          skills)
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-3082" data-mp="r0krkod73vy2">
                          {selectedModalPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-3083" data-mp="2wtanihdlk2gz">
                              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-3084" data-mp="2suts2v3jggfe">
                                Pathway
                              </span>
                              {pathway}
                            </span>)}
                        </div>
                      </div>}

                    {selectedLicenses.length > 0 && <div className={`pt-3 ${selectedModalPathways.length > 0 ? 'mt-3' : ''} border-t border-gray-200`} data-id="element-3085" data-mp="38849dko8ewy6">
                        <p className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-3086" data-mp="2bkzkvgn6nh8h">
                          Assigned Skills ({selectedLicenses.length})
                        </p>
                        <div className="flex flex-wrap gap-1.5" data-id="element-3087" data-mp="2ki9yh8nh3igk">
                          {selectedLicenses.map(skill => <span key={skill} className="px-2.5 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-3088" data-mp="2pc4ea0zyjdad">
                              {skill}
                            </span>)}
                        </div>
                      </div>}
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 mb-5 border border-amber-100" data-id="element-3089" data-mp="1p9ohul379lru">
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-3090" data-mp="1kbd3bx1ymn3a">
                      Once you click submit, the learner will receive an email
                      with instructions to create a password and complete their
                      registration.
                    </p>
                  </div>

                  <label className="flex items-start gap-3 mb-6 cursor-pointer" data-id="element-3091" data-mp="16cn9cqaft3j5">
                    <input type="checkbox" checked={acknowledgeChecked} onChange={e => setAcknowledgeChecked(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] mt-0.5" data-id="element-3092" data-mp="11k3wjvjkqcfe" />
                    <span className="text-sm text-gray-700" data-id="element-3093" data-mp="roae5vj6bopp">
                      I acknowledge that I have the authority to share the
                      information of the learner I provided.
                    </span>
                  </label>

                  <div className="flex gap-3" data-id="element-3094" data-mp="8yosxu434zb5">
                    <button onClick={() => setAddModalStep('form')} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-3095" data-mp="8xvo4yoosjvn">
                      Back
                    </button>
                    <button onClick={handleAddUser} disabled={!acknowledgeChecked} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${acknowledgeChecked ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3096" data-mp="3zkeanye69df">
                      Submit
                    </button>
                  </div>
                </>}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Bulk Upload Modal */}
      <AnimatePresence data-id="element-3097" data-mp="mwd8qnjapg9c">
        {showBulkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={handleCloseBulkModal} data-id="element-3098" data-mp="1anit9g06kwpz">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 w-full shadow-2xl max-h-[90vh] overflow-y-auto max-w-lg" onClick={e => e.stopPropagation()} data-id="element-3099" data-mp="1fgbx81nts110">
              <div className="flex items-center justify-between mb-6" data-id="element-3100" data-mp="2h60jr7ql9wh2">
                <div className="flex items-center gap-3" data-id="element-3101" data-mp="2h8egdl0x3gqq">
                  <div className="w-10 h-10 rounded-full bg-[#007558]/10 flex items-center justify-center" data-id="element-3102" data-mp="2c88u6kdf2mg1">
                    <Upload size={20} className="text-[#007558]" data-id="element-3103" data-mp="2q7rpgcl4q3ax" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-3104" data-mp="2l6ucceez7zga">
                    Bulk Upload Learners
                  </h2>
                </div>
                <button onClick={handleCloseBulkModal} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-3105" data-mp="hof1h00h013">
                  Close
                </button>
              </div>

              {bulkUploadStep === 'upload' ? <>
                  <div className="mb-5 rounded-lg border border-[#30718D]/20 bg-[#30718D]/5 p-4" data-id="element-3106" data-mp="pj3vhe9xd9pv">
                    <div className="flex items-start gap-3" data-id="element-3107" data-mp="tj61fzmp0pay">
                      <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-[#30718D]" aria-hidden="true" data-id="element-3108" data-mp="ybfp190c5da2" />
                      <div data-id="element-3109" data-mp="139h31fiwmmeo">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3110" data-mp="13cd3pbm4zp6t">Prepare your learner CSV</p>
                        <p className="mt-1 text-sm leading-relaxed text-gray-600" data-id="element-3111" data-mp="18bg76rmgxnco">
                          Include First Name, Last Name, and Email as column headers. Each completed row creates one learner without modules or pathways assigned.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button type="button" onClick={handleDownloadCsvTemplate} className="mb-5 flex w-full items-center justify-center gap-2 rounded-lg border border-[#30718D] bg-white px-4 py-2.5 text-sm font-semibold text-[#30718D] transition-colors hover:bg-[#30718D]/5" data-id="element-3112" data-mp="3j0mwpucu90g2">
                    <Download size={16} aria-hidden="true" data-id="element-3113" data-mp="31rszjn1upccq" />
                    Download CSV template
                  </button>

                  <input ref={bulkUploadInputRef} type="file" accept=".csv,text/csv" onChange={handleBulkFileChange} className="sr-only" data-id="element-3114" data-mp="2xriinfcit1j3" />
                  <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4" data-id="element-3115" data-mp="3uenhac7a9xmi">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-3116" data-mp="3pig90crf0a1e">
                      <div className="min-w-0" data-id="element-3117" data-mp="3le8l2hss6y48">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3118" data-mp="3ghi65xspq8ke">Upload your completed CSV</p>
                        <p className="mt-1 truncate text-sm text-gray-500" data-id="element-3119" data-mp="2a16e8eghhmx9">{bulkUploadFile ? bulkUploadFile.name : 'No file selected'}</p>
                      </div>
                      <button type="button" onClick={() => bulkUploadInputRef.current?.click()} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50" data-id="element-3120" data-mp="254667y02qa3i">
                        <FileSpreadsheet size={16} aria-hidden="true" data-id="element-3121" data-mp="1c3scvu1m45im" />
                        Choose CSV
                      </button>
                    </div>
                    {bulkUploadError && <p role="alert" className="mt-3 text-sm font-medium text-[#DB3725]" data-id="element-3122" data-mp="1lz3po6tgn2nq">{bulkUploadError}</p>}
                  </div>

                  <div className="flex gap-3" data-id="element-3123" data-mp="1h3wpva3bai43">
                    <button type="button" onClick={handleCloseBulkModal} className="flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50" data-id="element-3124" data-mp="1v0ab8uayv3nu">
                      Cancel
                    </button>
                    <button type="button" onClick={handleBulkImport} disabled={!bulkUploadFile} className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${bulkUploadFile ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'cursor-not-allowed bg-gray-200 text-gray-400'}`} data-id="element-3125" data-mp="3rtvbrpv3rwyt">
                      Upload CSV
                    </button>
                  </div>
                </> : <div className="py-4 text-center" data-id="element-3126" data-mp="22kujqa1z3lxo">
                  <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#007558]/10" data-id="element-3127" data-mp="22jb5eozvnkyj">
                    <CheckCircle size={28} className="text-[#007558]" aria-hidden="true" data-id="element-3128" data-mp="1on63xgmrj4zh" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-3129" data-mp="1fken11b4bh0g">Learners uploaded</h3>
                  <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-600" data-id="element-3130" data-mp="1fmhkg6elocf7">
                    {bulkImportedCount} learner{bulkImportedCount === 1 ? '' : 's'} {bulkImportedCount === 1 ? 'has' : 'have'} been added and selected in the table.
                  </p>
                  <div className="mt-5 rounded-lg border border-[#30718D]/20 bg-[#30718D]/5 p-4 text-left" data-id="element-3131" data-mp="3dz87lewq81dp">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-3132" data-mp="qph7ou4kdk2e">Next: assign learning</p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600" data-id="element-3133" data-mp="lr61ctdh9rox">
                      Close this window, then use the Assign Skills and Assign Pathways controls beneath the learner table to assign modules or pathways to the selected learners.
                    </p>
                  </div>
                  <button type="button" onClick={handleCloseBulkModal} className="mt-6 w-full rounded-lg bg-[#DB3725] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#c02f1f]" data-id="element-3134" data-mp="19k5y8brkjry1">
                    Go to learner table
                  </button>
                </div>}
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Generate Link Modal */}
      <AnimatePresence data-id="element-3135" data-mp="1t85bpudipd75">
        {showLinkModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkModal(false)} data-id="element-3136" data-mp="1oesmbi10l2dc">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-3137" data-mp="1vsbfa0y7z1n6">
              <div className="flex items-center justify-between mb-6" data-id="element-3138" data-mp="2en8hj75m28sf">
                <div className="flex items-center gap-3" data-id="element-3139" data-mp="2jmuwpm501z3v">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-3140" data-mp="2oemep8a3awie">
                    <Link2 size={20} className="text-purple-600" data-id="element-3141" data-mp="2teiip7isenxy" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-3142" data-mp="2xgchfa9f9hvh">
                    Learner Registration Link Generated
                  </h2>
                </div>
                <button onClick={() => setShowLinkModal(false)} className="text-sm font-semibold text-gray-700 hover:text-gray-900 underline transition-colors" data-id="element-3143" data-mp="32cuerp2fadxs">
                  Close
                </button>
              </div>

              <div className="mb-6" data-id="element-3144" data-mp="2yijcbbxam71b">
                <p className="text-sm text-gray-600 mb-4" data-id="element-3145" data-mp="2ygotc35l0ozv">
                  Share this link with learners to allow them to self-register
                  for the license(s) you select.
                </p>

                {/* Licenses Multi-Select */}
                <div className="mb-4" data-id="element-3146" data-mp="21s0ne5754s6t">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3147" data-mp="21t2871dykt1q">
                    Licenses <span className="text-[#DB3725]" data-id="element-3148" data-mp="2fsju98zvds7e">*</span>
                  </label>
                  <div className="relative" ref={linkLicenseDropdownRef} data-id="element-3149" data-mp="2as5ro6h1fagr">
                    <div className={`w-full px-4 py-2 border rounded-lg bg-white flex items-center justify-between cursor-text ${isLinkLicenseDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]' : 'border-gray-300'}`} onClick={() => setIsLinkLicenseDropdownOpen(true)} data-id="element-3150" data-mp="3r6f5i6nnlgtc">
                      <input type="text" placeholder={linkLicenses.length + linkPathways.length > 0 ? `${linkLicenses.length + linkPathways.length} item${linkLicenses.length + linkPathways.length > 1 ? 's' : ''} selected — type to search...` : 'Search and select pathways or modules'} value={linkLicenseSearch} onChange={e => {
                    setLinkLicenseSearch(e.target.value);
                    if (!isLinkLicenseDropdownOpen) setIsLinkLicenseDropdownOpen(true);
                  }} onFocus={() => setIsLinkLicenseDropdownOpen(true)} className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400" data-id="element-3151" data-mp="181xo34qqvqnb" />
                      <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isLinkLicenseDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3152" data-mp="1mwu1rxhbp67s" />
                    </div>
                    {isLinkLicenseDropdownOpen && <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden" data-id="element-3153" data-mp="2fdmvpgugfwwc">
                        <div className="max-h-72 overflow-y-auto" data-id="element-3154" data-mp="2p8lodmoza4g2">
                          {/* Pathways Section */}
                          {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length > 0 && <>
                              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3155" data-mp="2acvzngserd7n">
                                <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-3156" data-mp="26c2bqneytyer">
                                  Pathways
                                </span>
                                <button type="button" onClick={e => {
                          e.stopPropagation();
                          selectAllLinkPathways();
                        }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-3157" data-mp="1wlhhfc6brg1n">
                                  {linkPathways.length === pathwayData.length ? 'Deselect all' : 'Select all'}
                                </button>
                              </div>
                              {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-3158" data-mp="732jpz3hf5cd">
                                    <input type="checkbox" checked={linkPathways.includes(pathway.name)} onChange={() => toggleLinkPathway(pathway.name)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-3159" data-mp="c0b2a0sjlbe5" />
                                    <div data-id="element-3160" data-mp="3ki75oqsqbzvp">
                                      <div className="text-sm font-semibold text-gray-900" data-id="element-3161" data-mp="3kh5veojjfhf1">
                                        {pathway.name}
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-3162" data-mp="2rt9xjgi8fjgo">
                                        {pathway.skillCount} skills
                                      </div>
                                    </div>
                                  </label>)}
                            </>}

                          {/* Individual Skills Section */}
                          {availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length > 0 && <>
                              <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3163" data-mp="1u5ucqzdjmndl">
                                <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-3164" data-mp="245cura9w7z30">
                                  Individual Skills
                                </span>
                                <button type="button" onClick={e => {
                          e.stopPropagation();
                          selectAllLinkSkills();
                        }} className="text-xs font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-3165" data-mp="2hykpvbx6ncpg">
                                  {linkLicenses.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                                </button>
                              </div>
                              {availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-3166" data-mp="2wj6t4bzov0io">
                                    <input type="checkbox" checked={linkLicenses.includes(skill)} onChange={() => toggleLinkLicense(skill)} className="w-4 h-4 rounded border-gray-400 text-[#30718D] focus:ring-[#30718D]" data-id="element-3167" data-mp="2nqd4hnycqos5" />
                                    <span className="text-sm text-gray-700" data-id="element-3168" data-mp="2dus39s13xsjg">
                                      {skill}
                                    </span>
                                  </label>)}
                            </>}

                          {pathwayData.filter(p => p.name.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length === 0 && availableSkills.filter(skill => skill.toLowerCase().includes(linkLicenseSearch.toLowerCase())).length === 0 && <p className="px-4 py-3 text-sm text-gray-400" data-id="element-3169" data-mp="23vnz4hn7v3ru">
                                No results found
                              </p>}
                        </div>
                      </div>}
                  </div>
                  {(linkPathways.length > 0 || linkLicenses.length > 0) && <div className="flex flex-wrap gap-1.5 mt-2 max-h-24 overflow-y-auto" data-id="element-3170" data-mp="2rm01ze3luabk">
                      {linkPathways.map(pathway => <span key={pathway} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium" data-id="element-3171" data-mp="2mnghli4nt6lb">
                          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 mr-0.5" data-id="element-3172" data-mp="2cwls84w49qvn">
                            Pathway
                          </span>
                          {pathway}
                          <button type="button" onClick={() => toggleLinkPathway(pathway)} className="hover:text-[#DB3725] transition-colors" data-id="element-3173" data-mp="35krdcsr31hw0">
                            ×
                          </button>
                        </span>)}
                      {linkLicenses.map(skill => <span key={skill} className="inline-flex items-center gap-1 px-2 py-1 bg-[#30718D]/10 text-[#30718D] rounded text-xs font-medium" data-id="element-3174" data-mp="3hxbyn1j9cnju">
                          {skill}
                          <button type="button" onClick={() => toggleLinkLicense(skill)} className="hover:text-[#DB3725] transition-colors" data-id="element-3175" data-mp="3rrlq8h0dlhti">
                            ×
                          </button>
                        </span>)}
                    </div>}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-3176" data-mp="9g7csifu1w9k">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3177" data-mp="edqu5r7tup5q">
                    <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-3178" data-mp="je52799fhz3r">
                      Registration Link
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${userType === 'learners' ? 'bg-[#30718D]/10 text-[#30718D]' : 'bg-purple-100 text-purple-700'}`} data-id="element-3179" data-mp="nfzyfzci5mao">
                      Learner
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3180" data-mp="2t86718hw8q09">
                    <code className="flex-1 text-sm text-gray-900 break-all" data-id="element-3181" data-mp="2t9pov3j77945">
                      {generatedLink}
                    </code>
                    <button onClick={handleCopyLink} className="p-2 text-[#30718D] hover:bg-[#30718D]/10 rounded-lg transition-colors flex-shrink-0" data-id="element-3182" data-mp="336ca3tffs26p">
                      {linkCopied ? <CheckCircle size={18} data-id="element-3183" data-mp="2y9lqiw4faogj" /> : <Copy size={18} data-id="element-3184" data-mp="3c70mkjp6luhh" />}
                    </button>
                  </div>
                </div>

                {linkCopied && <motion.p initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-sm text-[#007558] mt-2 flex items-center gap-1" data-id="element-3185" data-mp="386qg75xh9223">
                    <CheckCircle size={14} data-id="element-3186" data-mp="1dtqviye8phny" />
                    Link copied to clipboard!
                  </motion.p>}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-6" data-id="element-3187" data-mp="17puc16dif058">
                <div className="flex items-start gap-3" data-id="element-3188" data-mp="17rfah72y338d">
                  <Clock size={20} className="text-purple-600 mt-0.5" data-id="element-3189" data-mp="twv0tgkrfs3h" />
                  <div data-id="element-3190" data-mp="ywhbbi97eu8l">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-3191" data-mp="yvz8g2s1dhrp">
                      Link Expiration
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-3192" data-mp="k1uosgfeprve">
                      This link will expire in 7 days. You can generate a new
                      link at any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-3193" data-mp="fz6fk7ms6ixw">
                <button onClick={() => {
              setShowLinkModal(false);
              setIsLinkLicenseDropdownOpen(false);
              setLinkLicenseSearch('');
              setLinkPathways([]);
              setLinkLicenses([]);
            }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-3194" data-mp="fzzlx7htv6fi">
                  Close
                </button>
                <button onClick={handleGenerateLink} disabled={linkLicenses.length === 0 && linkPathways.length === 0} className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium shadow-sm ${linkLicenses.length > 0 || linkPathways.length > 0 ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3195" data-mp="b7elghs5cla">
                  Generate New Link
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Floating Action Bar for Bulk Assignment */}
      <AnimatePresence data-id="element-3196" data-mp="38tdb42yb6ocr">
        {selectedUsers.length > 0 && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] px-4 py-4 sm:px-6 lg:px-8" data-id="element-3197" data-mp="34qgrc8x18m78">
            <div className="max-w-[1440px] mx-auto flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4" data-id="element-3198" data-mp="1k6lesu0ylj99">
              {/* Left side: badge + dropdowns */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1" data-id="element-3199" data-mp="1t94dd1xpp9pp">
                <div className="bg-[#30718D]/10 text-[#30718D] px-3 py-2 rounded-lg font-semibold text-sm whitespace-nowrap text-center sm:text-left" data-id="element-3200" data-mp="wivchd91nry9">
                  {selectedUsers.length} selected
                </div>

                {/* Assign Skills Dropdown */}
                <div className="relative flex-1 sm:max-w-[240px]" ref={bulkSkillDropdownRef} data-id="element-3201" data-mp="15h6zl5apc7ly">
                  <button onClick={() => {
                if (noLicensesAvailable) {
                  setShowNoLicenseToast(true);
                  setTimeout(() => setShowNoLicenseToast(false), 3000);
                  return;
                }
                setIsBulkSkillDropdownOpen(!isBulkSkillDropdownOpen);
                setIsBulkPathwayDropdownOpen(false);
              }} className={`w-full px-4 py-2 border rounded-lg flex items-center justify-between text-sm ${noLicensesAvailable ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : isBulkSkillDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-3202" data-mp="1abl1lqjvdczl">
                    <span className="truncate text-gray-700" data-id="element-3203" data-mp="127bsecpopqaw">
                      {bulkSelectedSkills.length > 0 ? `${bulkSelectedSkills.length} Skills selected` : 'Assign Skills...'}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isBulkSkillDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3204" data-mp="167c800ois7jj" />
                  </button>

                  {isBulkSkillDropdownOpen && <div className="absolute bottom-full mb-2 left-0 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-3205" data-mp="2hmr4l3ctgxe4">
                      <div className="max-h-80 overflow-y-auto" data-id="element-3206" data-mp="2q5civmpvz9ty">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3207" data-mp="2u5mpn1li649v">
                          <span className="text-xs font-medium text-gray-900" data-id="element-3208" data-mp="2g6wwfx1t0oqs">
                            Individual Skills
                          </span>
                          <button onClick={() => toggleBulkSkill('all')} className="text-xs font-medium text-[#30718D] hover:text-[#265a70]" data-id="element-3209" data-mp="38thnewedaytt">
                            {bulkSelectedSkills.length === availableSkills.length ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                        {availableSkills.map(skill => <label key={skill} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0" data-id="element-3210" data-mp="340ofjznlradi">
                            <input type="checkbox" checked={bulkSelectedSkills.includes(skill)} onChange={() => toggleBulkSkill(skill)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-3211" data-mp="eprbcmxzm75" />
                            <span className="text-sm text-gray-700" data-id="element-3212" data-mp="1rp1e04el2u53">
                              {skill}
                            </span>
                          </label>)}
                      </div>
                    </div>}
                </div>

                {/* Assign Pathways Dropdown */}
                <div className="relative flex-1 sm:max-w-[240px]" ref={bulkPathwayDropdownRef} data-id="element-3213" data-mp="2ff5ew40e0fse">
                  <button onClick={() => {
                if (noLicensesAvailable) {
                  setShowNoLicenseToast(true);
                  setTimeout(() => setShowNoLicenseToast(false), 3000);
                  return;
                }
                setIsBulkPathwayDropdownOpen(!isBulkPathwayDropdownOpen);
                setIsBulkSkillDropdownOpen(false);
              }} className={`w-full px-4 py-2 border rounded-lg flex items-center justify-between text-sm ${noLicensesAvailable ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : isBulkPathwayDropdownOpen ? 'border-[#30718D] ring-2 ring-[#30718D]/20 bg-white' : 'border-gray-300 hover:border-gray-400 bg-white'}`} data-id="element-3214" data-mp="2ahn4082t7qhr">
                    <span className="truncate text-gray-700" data-id="element-3215" data-mp="3anqt6kx6u0dy">
                      {bulkSelectedPathways.length > 0 ? `${bulkSelectedPathways.length} Pathways selected` : 'Assign Pathways...'}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform flex-shrink-0 ml-2 ${isBulkPathwayDropdownOpen ? 'rotate-180' : ''}`} data-id="element-3216" data-mp="3pdy50hsowl5o" />
                  </button>

                  {isBulkPathwayDropdownOpen && <div className="absolute bottom-full mb-2 left-0 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-50" data-id="element-3217" data-mp="29qnzyoezxb8">
                      <div className="max-h-80 overflow-y-auto" data-id="element-3218" data-mp="2013366xu27kx">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-3219" data-mp="241d6tcy5klwm">
                          <span className="text-xs font-medium text-gray-900" data-id="element-3220" data-mp="1pl2iyjs4i9ml">
                            Pathways
                          </span>
                          <button onClick={() => toggleBulkPathway('all')} className="text-xs font-medium text-[#30718D] hover:text-[#265a70]" data-id="element-3221" data-mp="1gm8niwqj3pam">
                            {bulkSelectedPathways.length === pathwayData.map(p => p.name).length ? 'Deselect all' : 'Select all'}
                          </button>
                        </div>
                        {pathwayData.map(pathway => <label key={pathway.name} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100" data-id="element-3222" data-mp="2d9lq2vzfsgd3">
                            <input type="checkbox" checked={bulkSelectedPathways.includes(pathway.name)} onChange={() => toggleBulkPathway(pathway.name)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D]" data-id="element-3223" data-mp="24a8armomsxkg" />
                            <div data-id="element-3224" data-mp="su1mhk9uyu4v">
                              <div className="text-sm font-bold text-gray-900" data-id="element-3225" data-mp="kekr74ewxhzr">
                                {pathway.name}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-3226" data-mp="gfldaub7eaw6">
                                {pathway.skillCount} skills
                              </div>
                            </div>
                          </label>)}
                      </div>
                    </div>}
                </div>
              </div>

              {/* Right side: Cancel + Assign */}
              <div className="flex items-center justify-center lg:justify-end gap-3" data-id="element-3227" data-mp="3swiatyzbjog1">
                <button onClick={() => {
              setSelectedUsers([]);
              setBulkSelectedSkills([]);
              setBulkSelectedPathways([]);
            }} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3228" data-mp="bfh4lqgjekgi">
                  Cancel
                </button>
                <button onClick={() => {
              if (noLicensesAvailable) {
                setShowNoLicenseToast(true);
                setTimeout(() => setShowNoLicenseToast(false), 3000);
                return;
              }
              handleBulkAssign();
            }} disabled={noLicensesAvailable || bulkSelectedSkills.length === 0 && bulkSelectedPathways.length === 0} className={`flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${noLicensesAvailable ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : bulkSelectedSkills.length > 0 || bulkSelectedPathways.length > 0 ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-3229" data-mp="3a45xkh74n2r4">
                  <CheckCircle size={16} data-id="element-3230" data-mp="1926see0wpo3u" />
                  Assign Selected
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}